import { Box } from "@chakra-ui/layout";
import { Text, RadioGroup, Radio, Flex, Switch } from "@chakra-ui/react";
import { Row, Col } from "antd";
import { showErrorAxios } from "infra/helpers/alerta";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import DefaultButton from "ui/components/Button/PrimaryButton";
import Field from "../Field";
import {
  TenantContaResponse,
  TipoTenantContaEnum,
} from "core/features/contas_bancarias/typings";
import useCreateContaBancaria from "core/features/contas_bancarias/hooks/useCreateContaBancaria";
import SelectBanco from "../SelectBanco";
import OutlineButton from "ui/components/Button/OutlineButton";
import FormEmissaoBoleto from "./FormEmissaoBoleto";
import colors from "core/resources/theme/colors";
import { showConfirm, showError } from "../../../../infra/helpers/alerta";
import InputCpfCnpj from "../InputCpfCnpj";

function FormContaBancaria({
  onSuccess,
  onClose,
  visible,
  defaultValue,
  tenantId,
  id,
}: {
  onSuccess: (data: any) => Promise<void>;
  onClose: () => void;
  visible: boolean;
  defaultValue?: TenantContaResponse;
  tenantId?: string;
  id?: string;
}) {
  const { control, handleSubmit, reset, setValue, watch, formState } =
    useForm<TenantContaResponse>({
      reValidateMode: "onChange",
      mode: "onChange",
      defaultValues: {
        tipo: TipoTenantContaEnum.ContaCorrente,
        ativarPagamentoOnline: true,
      },
    });

  const { isLoading, mutate: handleSave } = useCreateContaBancaria(tenantId);

  const [banco, setBanco] = useState<any>(defaultValue?.banco);

  const form = watch();

  useEffect(() => {
    reset({
      ...defaultValue,
      ativarPagamentoOnline: !defaultValue
        ? true
        : defaultValue?.ativarPagamentoOnline,
    });
  }, [visible, defaultValue, reset]);

  const onSubmit = async (data: TenantContaResponse) => {
    if (data.desabilitadaAdmin) {
      showError("Não é possível editar uma conta bancária desabilitada");
      return;
    }

    let shouldSave: boolean = true;

    if (!defaultValue?.ativarPagamentoOnline && data.ativarPagamentoOnline) {
      const { isConfirmed } = await showConfirm(
        "Após ativar o pagamento online, não será possível desativar. Deseja continuar?"
      );
      shouldSave = isConfirmed;
    }

    if (!shouldSave) return;

    handleSave({ ...data, id } as any, {
      onSuccess: (res) => {
        reset();
        onSuccess(res.data.id);
      },
      onError: (res) => {
        showErrorAxios(res);
      },
    });
  };

  const isContaBancaria = watch("tipo") === TipoTenantContaEnum.ContaCorrente;
  const ativarPagamentoOnline = watch("ativarPagamentoOnline");

  return (
    <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
      <Text as="h3" fontSize={20} mb="16px" fontWeight="500">
        {form.desabilitadaAdmin ? `Visualizar` : id ? `Editar` : `Cadastrar`}{" "}
        conta bancária
      </Text>
      <Row>
        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Tipo de conta"
            control={control}
            name="tipo"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <RadioGroup
                value={field.value}
                onChange={field.onChange}
                as={Row}
                gutter={10}
                mb={6}
              >
                <Box as={Col} md={8}>
                  <Radio
                    isInvalid={isInvalid}
                    color={isInvalid ? "red" : undefined}
                    value={TipoTenantContaEnum.ContaCorrente}
                    disabled={form.desabilitadaAdmin}
                  >
                    Conta Bancária
                  </Radio>
                </Box>
                <Box as={Col} md={8}>
                  <Radio
                    isInvalid={isInvalid}
                    color={isInvalid ? "red" : undefined}
                    value={TipoTenantContaEnum.CaixaFisico}
                    disabled={form.desabilitadaAdmin}
                  >
                    Caixa Físico
                  </Radio>
                </Box>
              </RadioGroup>
            )}
          />
        </Box>
      </Row>
      {isContaBancaria ? (
        <Row>
          <Box as={Col} sm={24} mb={4}>
            <Field
              label="Banco"
              control={control}
              name="bancoId"
              shouldUnregister
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <SelectBanco
                  value={field.value}
                  disabled={form.desabilitadaAdmin}
                  onChange={(v, item) => {
                    field.onChange(v);

                    setBanco(item);

                    if (!watch("nome") && item) {
                      setValue("nome", item.nome);
                    }
                  }}
                  isInvalid={isInvalid}
                />
              )}
            />
          </Box>
        </Row>
      ) : null}
      <Row>
        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Nome da conta"
            control={control}
            name="nome"
            inputProps={{
              disabled: form.desabilitadaAdmin,
            }}
            rules={{
              required: true,
            }}
          />
        </Box>
      </Row>
      <Row>
        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Documento"
            control={control}
            name="documento"
            inputProps={{
              disabled: form.desabilitadaAdmin,
            }}
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <InputCpfCnpj
                isInvalid={isInvalid}
                placeholder="CPF/CNPJ"
                {...field}
              />
            )}
          />
        </Box>
      </Row>
      <Row gutter={16}>
        {id ? (
          <Box as={Col} sm={8} mb={4}>
            <Field
              label={"Saldo"}
              control={control}
              name="saldo"
              readonly={!!id}
              inputProps={{
                type: "money",
                color: watch("saldo") >= 0 ? undefined : "red",
                fontWeight: "bold",
                disabled: form.desabilitadaAdmin,
              }}
            />
          </Box>
        ) : null}

        <Box as={Col} sm={8} mb={4}>
          <Field
            label={"Saldo inicial"}
            control={control}
            name="saldoInicial"
            inputProps={{
              type: "money",
              color: watch("saldoInicial") >= 0 ? undefined : "red",
              fontWeight: "bold",
              disabled: form.desabilitadaAdmin,
            }}
          />
        </Box>

        <Box as={Col} sm={8} mb={4}>
          <Field
            label={"Data saldo inicial"}
            control={control}
            name="dataSaldoInicial"
            inputProps={{
              type: "date",
              disabled: form.desabilitadaAdmin,
            }}
          />
        </Box>
      </Row>
      {isContaBancaria ? (
        <Row gutter={16}>
          <Box as={Col} sm={16} mb={4}>
            <Field
              label="Agência"
              control={control}
              shouldUnregister
              name="agencia"
              inputProps={{
                placeholder: "Ex: 4321",
                type: "number",
                disabled: form.desabilitadaAdmin,
              }}
              rules={{
                required: true,
              }}
            />
          </Box>

          <Box as={Col} sm={8}>
            <Field
              label="Dígito da agência"
              control={control}
              shouldUnregister
              name="agenciaDigito"
              inputProps={{
                placeholder: "Ex: 9",
                type: "number",
                pattern: "[0-9]{1}",
                maxLength: 1,
                disabled: form.desabilitadaAdmin,
              }}
              rules={{
                maxLength: 1,
                pattern: {
                  message: "Deve ser um número",
                  value: /[0-9]{1}/,
                },
              }}
            />
          </Box>
        </Row>
      ) : null}
      {isContaBancaria ? (
        <Row gutter={16}>
          <Box as={Col} sm={16} mb={4}>
            <Field
              shouldUnregister
              label="Número"
              control={control}
              name="numero"
              inputProps={{
                placeholder: "Ex: 234123",
                type: "number",
                pattern: "[0-9]{1,12}",
                maxLength: 12,
                disabled: form.desabilitadaAdmin,
              }}
              rules={{
                required: true,
                pattern: {
                  message: "Deve ser um número com até 12 dígitos",
                  value: /[0-9]{1,12}/,
                },
              }}
            />
          </Box>

          <Box as={Col} sm={8}>
            <Field
              label="Dígito da conta"
              control={control}
              shouldUnregister
              name="contaDigito"
              inputProps={{
                placeholder: "Ex: 7",
                type: "number",
                pattern: "[0-9]{1}",
                maxLength: 1,
                disabled: form.desabilitadaAdmin,
              }}
              rules={{
                required: true,
                maxLength: 1,
                minLength: 1,
                pattern: {
                  message: "Deve ser um número",
                  value: /[0-9]{1}/,
                },
              }}
            />
          </Box>
        </Row>
      ) : null}
      {isContaBancaria ? (
        <FormEmissaoBoleto
          isEdit={!!id}
          control={control}
          watch={watch}
          bancoId={watch("bancoId")}
          banco={banco}
          setValue={setValue}
        />
      ) : null}
      {isContaBancaria &&
      !defaultValue?.ativarPagamentoOnline &&
      !form.desabilitadaAdmin ? (
        <Box
          p="16px"
          border="1px solid #ccc"
          borderRadius={8}
          borderColor={
            !!ativarPagamentoOnline ? colors.primary : colors.borderGray
          }
          marginTop={2}
        >
          <Field
            shouldUnregister
            control={control}
            name="ativarPagamentoOnline"
            render={({ field }) => {
              return (
                <Flex gap="8px">
                  <Switch
                    isChecked={!!field.value}
                    onChange={() => field.onChange(!field.value)}
                  />
                  <Text>Ativar pagamento online?</Text>
                </Flex>
              );
            }}
          />
        </Box>
      ) : null}

      <Box
        pt="32px"
        justifyContent="flex-end"
        d="flex"
        gridGap="8px"
        flexDir="row"
      >
        <OutlineButton
          disabled={isLoading}
          onClick={() => {
            onClose?.();
            reset();
          }}
        >
          {form.desabilitadaAdmin ? "Fechar" : "Cancelar"}
        </OutlineButton>

        {!form.desabilitadaAdmin ? (
          <DefaultButton
            type="button"
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || isLoading}
            minWidth={"120px"}
          >
            Salvar
          </DefaultButton>
        ) : null}
      </Box>
    </form>
  );
}

export default FormContaBancaria;
